// Credit for the aircraft registry (registration and type), required by its
// licence (ODC-By). The backend's RefreshWorldRegistry fills icao_aircraft_registry
// from it; see MictronicsAircraftDb. Shown on every map that shows aircraft.
export const AIRCRAFT_DATA_ATTRIBUTION =
  'Aircraft data &copy; <a href="https://github.com/Mictronics/aircraft-database">Mictronics</a> (ODC-By)';

// Map style configurations for tile layers
export const MAP_STYLES = {
  openstreetmap: {
    id: 'openstreetmap',
    name: 'Standard',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | ' + AIRCRAFT_DATA_ATTRIBUTION,
    subdomains: ['a', 'b', 'c']
  },
  'cartodb-dark': {
    id: 'cartodb-dark',
    name: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> | ' + AIRCRAFT_DATA_ATTRIBUTION,
    subdomains: ['a', 'b', 'c', 'd']
  },
  'cartodb-light': {
    id: 'cartodb-light',
    name: 'Light (Mono)',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> | ' + AIRCRAFT_DATA_ATTRIBUTION,
    subdomains: ['a', 'b', 'c', 'd']
  },
  'cartodb-voyager': {
    id: 'cartodb-voyager',
    name: 'Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a> | ' + AIRCRAFT_DATA_ATTRIBUTION,
    subdomains: ['a', 'b', 'c', 'd']
  }
};

// Default settings
export const DEFAULT_MAP_STYLE = 'openstreetmap';
export const DEFAULT_MAP_BRIGHTNESS = 60; // Percentage (0-100)

// Units configuration
export const UNIT_SYSTEMS = {
  metric: {
    id: 'metric',
    name: 'Metric',
    distance: 'km',
    distanceLong: 'kilometres',
    altitude: 'm',
    altitudeLong: 'metres',
    speed: 'km/h',
    speedLong: 'kilometres per hour',
    intervals: [20, 50, 100] // km
  },
  imperial: {
    id: 'imperial',
    name: 'Imperial',
    distance: 'mi',
    distanceLong: 'miles',
    altitude: 'ft',
    altitudeLong: 'feet',
    speed: 'mph',
    speedLong: 'miles per hour',
    intervals: [20, 50, 100] // miles
  },
  aeronautical: {
    id: 'aeronautical',
    name: 'Aeronautical',
    distance: 'nm',
    distanceLong: 'nautical miles',
    altitude: 'ft',
    altitudeLong: 'feet',
    speed: 'kts',
    speedLong: 'knots',
    intervals: [20, 50, 100] // nm
  }
};

export const DEFAULT_UNITS = 'metric';
export const DEFAULT_RANGE_RINGS_COUNT = 5;
export const DEFAULT_RANGE_RINGS_INTERVAL = 50; // in the selected unit

// Airport display options
export const AIRPORT_DISPLAY_OPTIONS = {
  none: { id: 'none', name: 'None', types: [] },
  large: { id: 'large', name: 'Major Airports', types: ['large_airport'] },
  medium: { id: 'medium', name: 'Medium & Major', types: ['large_airport', 'medium_airport'] },
  small: { id: 'small', name: 'All Airports', types: ['large_airport', 'medium_airport', 'small_airport'] },
  all: { id: 'all', name: 'All (incl. Heliports)', types: ['large_airport', 'medium_airport', 'small_airport', 'heliport'] }
};

export const DEFAULT_AIRPORT_DISPLAY = 'medium';
export const DEFAULT_AIRPORT_COLOUR = '#FFFFFF';

// Default colours
export const DEFAULT_COLOURS = {
  rangeRings: '#666666',
  maxDistanceOutline: '#1A5F5F',
  maxDistanceDots: '#5DD4D4', // Kept for backwards compatibility but uses maxDistanceOutline
  aircraftNormal: '#D4A520', // Yellow/gold
  aircraftNew: '#32CD32', // Green
  aircraftSelected: '#FF6B35', // Orange
  aircraftMilitary: '#C0C0C0', // Silver for military aircraft
  // Squawk 7500/7600/7700. Outranks every other marker colour, and keeps its hue
  // even in mono — an emergency is a safety signal, not a category. The marker also
  // carries a pulsing ring so it reads without relying on colour at all.
  aircraftEmergency: '#E02020',
  // When on, selecting an aircraft fits the map to its track (min 30 km span);
  // deselecting restores the previous view. Opt-in so it can't surprise anyone.
  zoomOnAircraftSelect: false,
  showRangeRings: true, // Toggle for the receiver's distance rings
  showRangeOutline: true, // Toggle for daily range outline visibility
  // All-time range settings
  allTimeRangeOutline: '#8B4513', // Saddle brown - distinct from daily range
  showAllTimeRangeOutline: true, // Toggle for all-time range outline visibility
  // NOTAM overlay
  showNotams: true, // Toggle for NOTAM overlay visibility
  // Airport ground vehicles (DAA trucks etc.) in the live map, History and Airport views
  showGroundVehicles: true
};

// Local storage keys
// Legacy: map style/brightness used to be global ('mapStyle' / 'mapBrightness')
// and are now per-theme (see THEME_MAP_APPEARANCE). 'mapColours' is still read
// once, to migrate old behaviour toggles into the new prefs store.
const STORAGE_KEY_COLOURS = 'mapColours';
const STORAGE_KEY_UNITS = 'displayUnits';
const STORAGE_KEY_RANGE_RINGS_COUNT = 'rangeRingsCount';
const STORAGE_KEY_RANGE_RINGS_INTERVAL = 'rangeRingsInterval';
const STORAGE_KEY_AIRPORT_SETTINGS = 'airportSettings';

// ───────────────────────────────────────────────────────────────────────────
// Theme-owned map appearance
//
// Previously map colours, style and brightness lived in ONE global blob that
// knew nothing about the active theme. Two consequences: the map never changed
// with the theme, and once anything wrote the blob — including a behaviour
// toggle like "show range rings" — the stored values were pinned forever and
// theme defaults could never reassert. That is the "too many dependencies /
// switching themes loses the right colours, logos, brightness" problem.
//
// Now:
//   • APPEARANCE (colours, map style, brightness) belongs to the THEME. Each
//     theme ships a complete set, and any user customisation is stored PER
//     THEME, so switching away and back restores that theme exactly.
//   • BEHAVIOUR (which overlays are on, zoom-on-select) is a global user
//     preference and deliberately does NOT follow the theme — changing theme
//     must never silently turn your NOTAM overlay off.
//
// Kept in this module rather than themeConfig.js so there is no import cycle:
// only the active theme ID is read across the boundary, via localStorage.
// ───────────────────────────────────────────────────────────────────────────

// Appearance keys owned by the theme. Everything else in DEFAULT_COLOURS is
// behaviour and lives in the global preference store.
const APPEARANCE_KEYS = [
  'rangeRings', 'maxDistanceOutline', 'maxDistanceDots', 'allTimeRangeOutline',
  'aircraftNormal', 'aircraftNew', 'aircraftSelected', 'aircraftMilitary', 'aircraftEmergency'
];

export const THEME_MAP_APPEARANCE = {
  // Dark UI, warm accents, plain OSM basemap dimmed down.
  standard: {
    mapStyle: 'openstreetmap', brightness: 60,
    rangeRings: '#666666', maxDistanceOutline: '#1A5F5F', maxDistanceDots: '#5DD4D4',
    allTimeRangeOutline: '#8B4513', aircraftNormal: '#D4A520', aircraftNew: '#32CD32',
    aircraftSelected: '#FF6B35', aircraftMilitary: '#C0C0C0', aircraftEmergency: '#E02020'
  },
  // Greyscale throughout: light basemap, near-neutral marks that still separate
  // by lightness rather than hue.
  mono: {
    mapStyle: 'cartodb-light', brightness: 95,
    rangeRings: '#9aa0a6', maxDistanceOutline: '#5f6368', maxDistanceDots: '#80868b',
    allTimeRangeOutline: '#3c4043', aircraftNormal: '#202124', aircraftNew: '#5f6368',
    aircraftSelected: '#000000', aircraftMilitary: '#9aa0a6',
    // Keeps its hue on purpose — see aircraftEmergency in DEFAULT_COLOURS.
    aircraftEmergency: '#C41010'
  },
  // Dark basemap with vibrant marks — maximum contrast for night use.
  dark: {
    mapStyle: 'cartodb-dark', brightness: 80,
    rangeRings: '#4a5568', maxDistanceOutline: '#14B8A6', maxDistanceDots: '#5EEAD4',
    allTimeRangeOutline: '#F97316', aircraftNormal: '#FFD700', aircraftNew: '#39FF14',
    aircraftSelected: '#FF6B35', aircraftMilitary: '#C0C0C0', aircraftEmergency: '#FF2D2D'
  },
  // Light basemap at full brightness, so marks are DARKENED to stay legible.
  light: {
    mapStyle: 'cartodb-light', brightness: 100,
    rangeRings: '#94a3b8', maxDistanceOutline: '#0F766E', maxDistanceDots: '#0D9488',
    allTimeRangeOutline: '#B45309', aircraftNormal: '#B45309', aircraftNew: '#15803D',
    aircraftSelected: '#EA580C', aircraftMilitary: '#64748B', aircraftEmergency: '#C81010'
  },
  // Custom starts from Standard; the user's own edits are stored per-theme
  // under the 'custom' slot like any other theme.
  custom: null // resolved to `standard` below
};

const STORAGE_KEY_THEME = 'appTheme';           // owned by themeConfig.js
const STORAGE_KEY_MAP_PREFS = 'mapPrefs';        // global behaviour flags
const STORAGE_KEY_APPEARANCE_PREFIX = 'mapAppearance:'; // per-theme overrides

function activeThemeId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_THEME);
    if (!raw) return 'standard';
    // themeConfig stores an object ({ themeId }) — tolerate a bare string too.
    const parsed = raw.trim().startsWith('{') ? JSON.parse(raw) : { themeId: raw };
    const id = parsed.themeId || 'standard';
    return THEME_MAP_APPEARANCE[id] !== undefined ? id : 'standard';
  } catch (_) {
    return 'standard';
  }
}

function themeAppearance(themeId) {
  return THEME_MAP_APPEARANCE[themeId] || THEME_MAP_APPEARANCE.standard;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

/** Per-theme appearance overrides the user has explicitly set. */
function themeOverrides(themeId) {
  return readJson(STORAGE_KEY_APPEARANCE_PREFIX + themeId, {}) || {};
}

/** Global behaviour flags (overlay toggles, zoom-on-select). */
function behaviourPrefs() {
  const stored = readJson(STORAGE_KEY_MAP_PREFS, null);
  if (stored) return stored;

  // One-time migration off the legacy single blob: keep the user's existing
  // toggles rather than silently resetting them.
  const legacy = readJson(STORAGE_KEY_COLOURS, null);
  if (legacy) {
    const prefs = {};
    Object.keys(DEFAULT_COLOURS).forEach(key => {
      if (!APPEARANCE_KEYS.includes(key) && legacy[key] !== undefined) {
        prefs[key] = legacy[key];
      }
    });
    try { localStorage.setItem(STORAGE_KEY_MAP_PREFS, JSON.stringify(prefs)); } catch (_) {}
    return prefs;
  }
  return {};
}

// Get map settings — style/brightness come from the active theme unless the
// user overrode them for that specific theme.
export function getMapSettings() {
  const themeId = activeThemeId();
  const base = themeAppearance(themeId);
  const override = themeOverrides(themeId);

  const style = override.mapStyle || base.mapStyle || DEFAULT_MAP_STYLE;
  const rawBrightness = override.brightness !== undefined ? override.brightness : base.brightness;
  const brightness = typeof rawBrightness === 'number' ? rawBrightness : DEFAULT_MAP_BRIGHTNESS;

  return {
    style: MAP_STYLES[style] ? style : DEFAULT_MAP_STYLE,
    brightness: Math.max(0, Math.min(100, brightness))
  };
}

// Save map settings as an override on the ACTIVE theme, so other themes keep
// their own look and this theme remembers yours.
export function saveMapSettings(settings) {
  const themeId = activeThemeId();
  const next = { ...themeOverrides(themeId) };
  if (settings.style && MAP_STYLES[settings.style]) next.mapStyle = settings.style;
  if (typeof settings.brightness === 'number') {
    next.brightness = Math.max(0, Math.min(100, settings.brightness));
  }
  try {
    localStorage.setItem(STORAGE_KEY_APPEARANCE_PREFIX + themeId, JSON.stringify(next));
  } catch (e) {
    console.error('Error saving map settings:', e);
  }
}

// Get colour settings: theme appearance -> this theme's overrides -> global
// behaviour flags. Shape is unchanged, so every consumer keeps working.
export function getColourSettings() {
  const themeId = activeThemeId();
  const base = themeAppearance(themeId);
  const override = themeOverrides(themeId);

  const appearance = {};
  APPEARANCE_KEYS.forEach(key => {
    appearance[key] = override[key] !== undefined ? override[key]
      : (base[key] !== undefined ? base[key] : DEFAULT_COLOURS[key]);
  });

  return { ...DEFAULT_COLOURS, ...appearance, ...behaviourPrefs() };
}

// Save colour settings, routing each key to the right store: appearance to the
// active theme, behaviour to the global prefs. This is what stops an overlay
// toggle from pinning the whole colour scheme.
export function saveColourSettings(colours) {
  if (!colours || typeof colours !== 'object') return;
  const themeId = activeThemeId();
  const base = themeAppearance(themeId);

  const appearanceOverride = { ...themeOverrides(themeId) };
  APPEARANCE_KEYS.forEach(key => {
    if (colours[key] === undefined) return;
    if (colours[key] === base[key]) {
      delete appearanceOverride[key]; // back to the theme default — stop overriding
    } else {
      appearanceOverride[key] = colours[key];
    }
  });

  const prefs = { ...behaviourPrefs() };
  Object.keys(DEFAULT_COLOURS).forEach(key => {
    if (!APPEARANCE_KEYS.includes(key) && colours[key] !== undefined) {
      prefs[key] = colours[key];
    }
  });

  try {
    localStorage.setItem(STORAGE_KEY_APPEARANCE_PREFIX + themeId, JSON.stringify(appearanceOverride));
    localStorage.setItem(STORAGE_KEY_MAP_PREFS, JSON.stringify(prefs));
  } catch (e) {
    console.error('Error saving colour settings:', e);
  }
}

/** Drop this theme's appearance overrides, returning it to its shipped look. */
export function resetThemeAppearance(themeId = activeThemeId()) {
  try {
    localStorage.removeItem(STORAGE_KEY_APPEARANCE_PREFIX + themeId);
  } catch (e) {
    console.error('Error resetting theme appearance:', e);
  }
}

// Get airport settings from localStorage
export function getAirportSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_AIRPORT_SETTINGS);
    if (stored) {
      const settings = JSON.parse(stored);
      return {
        display: AIRPORT_DISPLAY_OPTIONS[settings.display] ? settings.display : DEFAULT_AIRPORT_DISPLAY,
        colour: settings.colour || DEFAULT_AIRPORT_COLOUR
      };
    }
  } catch (e) {
    console.error('Error reading airport settings:', e);
  }
  return {
    display: DEFAULT_AIRPORT_DISPLAY,
    colour: DEFAULT_AIRPORT_COLOUR
  };
}

// Save airport settings to localStorage
export function saveAirportSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY_AIRPORT_SETTINGS, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving airport settings:', e);
  }
}

// Get the tile layer configuration for current settings
export function getTileLayerConfig() {
  const settings = getMapSettings();
  const styleConfig = MAP_STYLES[settings.style] || MAP_STYLES[DEFAULT_MAP_STYLE];

  return {
    ...styleConfig,
    brightness: settings.brightness
  };
}

// Generate CSS filter string based on brightness
export function getMapFilter(brightness) {
  // brightness: 0 = very dark, 100 = full brightness
  const brightnessValue = brightness / 100;
  // Adjust contrast slightly for better visibility at lower brightness
  const contrastValue = 1 + (1 - brightnessValue) * 0.2;
  // Reduce saturation slightly at lower brightness
  const saturationValue = 0.8 + (brightnessValue * 0.2);

  return `brightness(${brightnessValue.toFixed(2)}) contrast(${contrastValue.toFixed(2)}) saturate(${saturationValue.toFixed(2)})`;
}

// Pre-computed CSS filter values for specific colours
// These are manually tuned to produce vibrant colours on white sprites
const COLOUR_FILTERS = {
  // Yellow/gold - #D4A520 - bright and saturated
  '#D4A520': 'brightness(0) saturate(100%) invert(70%) sepia(90%) saturate(1500%) hue-rotate(12deg) brightness(100%) contrast(100%)',
  // Green - #32CD32 - vibrant lime green
  '#32CD32': 'brightness(0) saturate(100%) invert(65%) sepia(100%) saturate(600%) hue-rotate(75deg) brightness(100%) contrast(100%)',
  // Orange - #FF6B35 - bright orange
  '#FF6B35': 'brightness(0) saturate(100%) invert(55%) sepia(100%) saturate(3000%) hue-rotate(345deg) brightness(105%) contrast(100%)',
  // Red - #FF0000
  '#FF0000': 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(7500%) hue-rotate(0deg) brightness(105%) contrast(115%)',
  // Blue - #0066FF
  '#0066FF': 'brightness(0) saturate(100%) invert(30%) sepia(100%) saturate(3000%) hue-rotate(210deg) brightness(105%) contrast(100%)',
  // Cyan - #00FFFF
  '#00FFFF': 'brightness(0) saturate(100%) invert(80%) sepia(60%) saturate(800%) hue-rotate(130deg) brightness(105%) contrast(100%)',
  // White - #FFFFFF
  '#FFFFFF': 'brightness(0) saturate(100%) invert(100%)',
  // Silver - #C0C0C0 - for military aircraft (brighter, more metallic look)
  '#C0C0C0': 'brightness(0) saturate(100%) invert(85%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)',
};

// Convert hex colour to CSS filter for aircraft icons
// Uses pre-computed filters for known colours, or calculates for custom colours
export function getAircraftColourFilter(hexColour) {
  // Normalize hex colour to uppercase with hash
  const normalizedHex = hexColour.toUpperCase().startsWith('#')
    ? hexColour.toUpperCase()
    : '#' + hexColour.toUpperCase();

  // Check if we have a pre-computed filter for this exact colour
  if (COLOUR_FILTERS[normalizedHex]) {
    return {
      filter: COLOUR_FILTERS[normalizedHex]
    };
  }

  // For custom colours, calculate filter values
  const hex = normalizedHex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert to HSL
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
        break;
      case gNorm:
        h = ((bNorm - rNorm) / d + 2) / 6;
        break;
      case bNorm:
        h = ((rNorm - gNorm) / d + 4) / 6;
        break;
      default:
        break;
    }
  }

  // Calculate filter values based on HSL
  const hueDeg = Math.round(h * 360);
  const satPercent = Math.round(s * 100);
  const lightPercent = Math.round(l * 100);

  // Invert percentage based on lightness (lighter colours need higher invert)
  const invertPercent = Math.round(lightPercent * 0.8 + 20);
  // Sepia helps shift hue from grayscale
  const sepiaPercent = Math.min(100, satPercent + 50);
  // Saturate to bring out colour intensity
  const saturatePercent = Math.round(500 + satPercent * 10);
  // Hue rotate to target colour
  const hueRotate = hueDeg;
  // Brightness adjustment
  const brightness = 0.9 + l * 0.2;

  return {
    filter: `brightness(0) saturate(100%) invert(${invertPercent}%) sepia(${sepiaPercent}%) saturate(${saturatePercent}%) hue-rotate(${hueRotate}deg) brightness(${brightness.toFixed(2)})`
  };
}

// Get units setting from localStorage
export function getUnitsSettings() {
  const units = localStorage.getItem(STORAGE_KEY_UNITS) || DEFAULT_UNITS;
  const rawCount = localStorage.getItem(STORAGE_KEY_RANGE_RINGS_COUNT);
  const rawInterval = localStorage.getItem(STORAGE_KEY_RANGE_RINGS_INTERVAL);
  const rangeRingsCount = parseInt(rawCount, 10);
  const rangeRingsInterval = parseInt(rawInterval, 10);

  return {
    units: UNIT_SYSTEMS[units] ? units : DEFAULT_UNITS,
    rangeRingsCount: isNaN(rangeRingsCount) ? DEFAULT_RANGE_RINGS_COUNT : Math.max(1, Math.min(10, rangeRingsCount)),
    rangeRingsInterval: isNaN(rangeRingsInterval) ? DEFAULT_RANGE_RINGS_INTERVAL : rangeRingsInterval
  };
}

// Save units settings to localStorage
export function saveUnitsSettings(settings) {
  if (settings.units && UNIT_SYSTEMS[settings.units]) {
    localStorage.setItem(STORAGE_KEY_UNITS, settings.units);
  }
  if (typeof settings.rangeRingsCount === 'number') {
    const count = Math.max(1, Math.min(10, settings.rangeRingsCount));
    localStorage.setItem(STORAGE_KEY_RANGE_RINGS_COUNT, count.toString());
  }
  if (typeof settings.rangeRingsInterval === 'number') {
    localStorage.setItem(STORAGE_KEY_RANGE_RINGS_INTERVAL, settings.rangeRingsInterval.toString());
  }
}

// Unit conversion functions
// All internal values are stored in metric (km for distance, metres for altitude from backend is actually feet)
// Backend sends altitude in feet, distance in km

// Convert km to other distance units
export function convertDistance(valueKm, toUnit) {
  if (valueKm == null || isNaN(valueKm)) return null;
  switch (toUnit) {
    case 'metric':
      return valueKm; // km
    case 'imperial':
      return valueKm * 0.621371; // miles
    case 'aeronautical':
      return valueKm * 0.539957; // nautical miles
    default:
      return valueKm;
  }
}

// Convert feet to altitude units (backend sends feet)
export function convertAltitude(valueFeet, toUnit) {
  if (valueFeet == null || isNaN(valueFeet)) return null;
  switch (toUnit) {
    case 'metric':
      return valueFeet * 0.3048; // metres
    case 'imperial':
    case 'aeronautical':
      return valueFeet; // feet
    default:
      return valueFeet;
  }
}

// Convert range ring interval from selected unit to km (for map rendering)
export function intervalToKm(interval, fromUnit) {
  switch (fromUnit) {
    case 'metric':
      return interval; // already km
    case 'imperial':
      return interval * 1.60934; // miles to km
    case 'aeronautical':
      return interval * 1.852; // nm to km
    default:
      return interval;
  }
}

// Format distance with unit label
export function formatDistance(valueKm, unitSystem, decimals = 1) {
  const converted = convertDistance(valueKm, unitSystem);
  if (converted == null) return '-';
  const unit = UNIT_SYSTEMS[unitSystem]?.distance || 'km';
  return `${converted.toFixed(decimals)} ${unit}`;
}

// Format altitude with unit label
export function formatAltitude(valueFeet, unitSystem) {
  const converted = convertAltitude(valueFeet, unitSystem);
  if (converted == null) return '-';
  const unit = UNIT_SYSTEMS[unitSystem]?.altitude || 'ft';
  // No decimals for altitude, use thousands separator
  return `${Math.round(converted).toLocaleString()} ${unit}`;
}

// Convert speed (knots to appropriate unit)
export function convertSpeed(valueKnots, toUnit) {
  if (valueKnots == null || isNaN(valueKnots)) return null;
  switch (toUnit) {
    case 'metric':
      // knots to km/h: 1 knot = 1.852 km/h
      return valueKnots * 1.852;
    case 'imperial':
      // knots to mph: 1 knot = 1.15078 mph
      return valueKnots * 1.15078;
    case 'aeronautical':
      return valueKnots; // already in knots
    default:
      return valueKnots;
  }
}

// Convert vertical speed (feet per minute to metres per second for metric)
export function convertVerticalSpeed(valueFpm, toUnit) {
  if (valueFpm == null || isNaN(valueFpm)) return null;
  switch (toUnit) {
    case 'metric':
      // fpm to m/s: divide by 196.85 (1 m/s = 196.85 fpm)
      return valueFpm / 196.85;
    case 'imperial':
    case 'aeronautical':
      return valueFpm; // feet per minute
    default:
      return valueFpm;
  }
}

// Get vertical speed unit label
export function getVerticalSpeedUnit(unitSystem) {
  switch (unitSystem) {
    case 'metric':
      return 'm/s';
    case 'imperial':
    case 'aeronautical':
    default:
      return 'fpm';
  }
}
